/**
 * /settings — perfil, roles y estado de la conexión.
 */
import { PageHeader } from '@/components/app-shell'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import { StatusPill } from '@/components/ui/status-pill'
import { EmailSettingsPanel } from '@/components/email-settings-panel'
import { UsersPanel } from '@/components/users-panel'
import { useSession } from '@/lib/session'

export function SettingsPage() {
  const { user, status, signOut, refresh } = useSession()

  return (
    <>
      <PageHeader title="Settings" subtitle="Tu cuenta, roles y conexión a la base" />

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Solo admin: el listado de usuarios dice a quién atacar para entrar. */}
        {user?.roles.includes('admin') && <EmailSettingsPanel />}
        {user?.roles.includes('admin') && <UsersPanel currentUserId={user.id} />}

        <Card>
          <CardHeader title="Perfil" />
          <CardBody className="space-y-3 text-sm">
            <Row label="Nombre" value={user?.fullName ?? '—'} />
            <Row label="Email" value={user?.email ?? '—'} />
            <div className="flex flex-wrap gap-2">
              {(user?.roles ?? []).map((r) => (
                <StatusPill key={r} status={r} tone="info" />
              ))}
              {(user?.roles?.length ?? 0) === 0 && (
                <span className="text-muted-foreground">Sin roles</span>
              )}
            </div>
            <Button type="button" variant="outline" onClick={() => void signOut()}>
              Sign out
            </Button>
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Base de datos"
            action={
              <Button type="button" size="sm" variant="ghost" onClick={() => void refresh()}>
                Refresh
              </Button>
            }
          />
          <CardBody className="space-y-2 text-sm">
            <Row
              label="Estado"
              value={status?.connected ? 'Conectada' : 'Sin conexión'}
            />
            <Row label="Origen" value={status?.source ?? '—'} />
            <Row label="Plataforma" value={status?.platform ?? '—'} />
            <Row
              label="Esquema"
              value={status?.schemaReady ? 'Listo' : 'Pendiente de migrar'}
            />
            <Row label="Versión PG" value={status?.serverVersion ?? '—'} />
            {status?.editable && (
              <p className="pt-2 text-xs text-muted-foreground">
                En self-hosted puedes reconfigurar la conexión desde el flujo de setup
                (solo admin, si no hay usuarios o con rol admin).
              </p>
            )}
            {!status?.editable && (
              <p className="pt-2 text-xs text-muted-foreground">
                En Vercel la conexión viene de <code>DATABASE_URL</code> (Neon pooler).
              </p>
            )}
          </CardBody>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader title="Backup" />
          <CardBody className="text-sm text-muted-foreground">
            <p>
              Programa un <code className="text-foreground">pg_dump</code> diario hacia otra
              máquina. Ejemplo:
            </p>
            <pre className="mt-2 overflow-x-auto rounded-xl bg-muted p-3 text-xs text-foreground">
{`pg_dump --format=custom --file=gkdfleet-$(date +%F).dump \\
  "$DATABASE_URL"`}
            </pre>
          </CardBody>
        </Card>
      </div>
    </>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium break-all">{value}</span>
    </div>
  )
}
